/**
 * InvoiceTab — generated government invoice + payment status.
 *
 * Shows the invoice as a PDF preview, signature envelope status (since
 * the PSPC invoice flow requires e-sign), and a payment-status card.
 */
import React from 'react';
import PdfPreview from '../components/PdfPreview';
import SignatureFlow from '../components/SignatureFlow';
import { useManifest } from '../ManifestContext';
import { getOrderMocks } from '../mocks';
import { Receipt, Wallet, Send } from 'lucide-react';

const formatCurrency = (n) => `$${n?.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const InvoiceTab = () => {
  const { activeOrder } = useManifest();
  const mocks = getOrderMocks(activeOrder.id);
  const inv = mocks.invoice;
  const invoiceEnvelope = mocks.signatures.find((s) => s.title.toLowerCase().includes('invoice'));

  if (!inv) {
    return (
      <div className="manifest-tab-pane">
        <div className="manifest-empty manifest-empty-inline">
          <Receipt size={24} />
          <h3>Invoice not yet generated</h3>
          <p>Once goods are delivered and proof-of-delivery is on file, the Co-Pilot auto-generates the government invoice.</p>
          <button className="manifest-btn-primary-sm">Generate invoice now</button>
        </div>
      </div>
    );
  }

  return (
    <div className="manifest-tab-pane">
      <div className="manifest-insights-row">
        <div className="manifest-insight-card phase-6">
          <div className="manifest-insight-icon"><Receipt size={16} /></div>
          <div>
            <div className="manifest-insight-label">Invoice number</div>
            <div className="manifest-insight-value">{inv.invoiceNumber}</div>
            <div className="manifest-insight-sub">Issued {formatDate(inv.issuedAt)}</div>
          </div>
        </div>
        <div className="manifest-insight-card phase-6">
          <div className="manifest-insight-icon"><Send size={16} /></div>
          <div>
            <div className="manifest-insight-label">Submission</div>
            <div className="manifest-insight-value">{inv.status === 'submitted' ? 'Submitted' : 'Draft'}</div>
            <div className="manifest-insight-sub">PSPC iSupplier portal</div>
          </div>
        </div>
        <div className="manifest-insight-card phase-6">
          <div className="manifest-insight-icon"><Wallet size={16} /></div>
          <div>
            <div className="manifest-insight-label">Payment</div>
            <div className="manifest-insight-value">{formatCurrency(inv.total)} CAD</div>
            <div className="manifest-insight-sub">Net {30} · expected {formatDate(inv.dueAt)}</div>
          </div>
        </div>
      </div>

      <div className="invoice-tab-grid">
        <PdfPreview
          title="Invoice"
          fileName={`${inv.invoiceNumber}.pdf`}
          badge={inv.status === 'submitted' ? 'Submitted' : 'Draft'}
        >
          <div className="invoice-pdf-head">
            <div className="invoice-pdf-issuer">
              <div className="invoice-pdf-issuer-name">{inv.issuer.name}</div>
              <div className="invoice-pdf-issuer-line">{inv.issuer.address}</div>
              <div className="invoice-pdf-issuer-line">{inv.issuer.gstNumber}</div>
            </div>
            <div className="invoice-pdf-meta">
              <div className="invoice-pdf-num">{inv.invoiceNumber}</div>
              <div><strong>Issued:</strong> {formatDate(inv.issuedAt)}</div>
              <div><strong>Due:</strong> {formatDate(inv.dueAt)}</div>
            </div>
          </div>
          <div className="pdf-divider" />
          <div className="invoice-pdf-billto">
            <div className="invoice-pdf-billto-label">BILL TO</div>
            <div className="invoice-pdf-billto-name">{inv.billTo.name}</div>
            <div>{inv.billTo.attn}</div>
            <div>{inv.billTo.address}</div>
            <div><strong>Contract:</strong> {inv.billTo.contractNumber}</div>
          </div>
          <table className="invoice-pdf-table">
            <thead>
              <tr>
                <th>Description</th>
                <th className="num">Qty</th>
                <th className="num">Unit (CAD)</th>
                <th className="num">Total</th>
              </tr>
            </thead>
            <tbody>
              {inv.lineItems.map((li, i) => (
                <tr key={i}>
                  <td>{li.description}</td>
                  <td className="num">{li.qty.toLocaleString()}</td>
                  <td className="num">{formatCurrency(li.unitCad)}</td>
                  <td className="num">{formatCurrency(li.lineTotal)}</td>
                </tr>
              ))}
              <tr className="invoice-pdf-subtotal">
                <td colSpan={3}>Subtotal</td>
                <td className="num">{formatCurrency(inv.subtotal)}</td>
              </tr>
              <tr>
                <td colSpan={3}>GST/HST ({inv.gstHstPct}%) — waived (ITB)</td>
                <td className="num">{formatCurrency(inv.gstHstAmount)}</td>
              </tr>
              <tr className="invoice-pdf-total">
                <td colSpan={3}>Total</td>
                <td className="num">{formatCurrency(inv.total)}</td>
              </tr>
            </tbody>
          </table>
          <div className="pdf-divider" />
          <p className="invoice-pdf-notes">{inv.notes}</p>
          <div className="invoice-pdf-bank">
            <div className="invoice-pdf-bank-label">Remit payment to</div>
            <div>{inv.issuer.bankInfo}</div>
          </div>
        </PdfPreview>

        <div className="invoice-tab-side">
          {invoiceEnvelope && (
            <>
              <div className="manifest-section-head">
                <h3>Approvals</h3>
                <span>E-signature flow</span>
              </div>
              <SignatureFlow envelope={invoiceEnvelope} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceTab;
